import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, omit, uniq } from 'lodash';
import {
  Segment, Header, Button, Icon, Form, Select, Checkbox, Message, Loader, TextArea,
} from 'semantic-ui-react';

import client from '../../apollo';
import subscriptionPreferenceQuery from '../../graphql/subscriptionPreferenceQuery';
import preferencesDiscoverQuery from '../../graphql/preferencesDiscoverQuery';
import updatePreferencesMutation from '../../graphql/updatePreferencesMutation';
import PreferencePatterns from '../../constants/PreferencePatterns';
import { isValidFacts } from '../../utilities/preferenceUtil';

// Same generic subscription_preferences mechanism every individual dealer subscription
// already uses (see Subscription > Preferences) — a master is just a subscription under
// the hood, so the same GraphQL round-trip (subscriptionPreferenceQuery /
// preferencesDiscoverQuery / updatePreferencesMutation) works unmodified by passing the
// master's id as the subscriptionId. Nested AND/OR groups (innerRuleSetList) are not
// editable here, but they are carried through a save untouched: tappy rebuilds the APMS
// tree by looking every rule-set item up in ruleList, then innerRuleSetList, and an item
// found in neither fails the whole save.
//
// Client-made uids carry a `new-` prefix so they can never equal one tappy minted on load
// (`rs0`, `rs0-ru0`, `rs0-ir0`, ...). A bare counter would hand out `rs1` for the first
// added rule set, the same uid as the second loaded one, and every edit/remove keyed by
// uid would then hit both. tappy only matches uids against each other on save, so any
// string works.
let uidSeq = 0;
const nextUid = prefix => {
  uidSeq += 1;
  return `new-${prefix}-${uidSeq}`;
};

const EMPTY_RULE = () => ({
  uid: nextUid('ru'), ruleType: '', facts: [], operatorType: 'EQUALS', include: true,
});

// APMS compares facts verbatim (LOCATION_CODE) or Long.parseLong's them (PARTNER_CHANNEL),
// so " FAA" never matches and " 34" throws on the sale. Every fact is trimmed and empties
// (a trailing comma, ",,") dropped before it reaches rule state or the save payload.
export const normalizeFactList = facts => uniq(
  (facts || []).map(fact => String(fact).trim()).filter(fact => fact !== '')
);

export const normalizeFacts = text => normalizeFactList(String(text || '').split(','));

const FACT_PATTERN_RULE_TYPES = [
  ...PreferencePatterns.RULE_TYPE_FACTS_NUMBER,
  ...PreferencePatterns.RULE_TYPE_FACTS_CHARACTER,
  ...PreferencePatterns.RULE_TYPE_FACTS_CHARACTER_WITH_SPACE,
  ...PreferencePatterns.RULE_TYPE_FACTS_YES_NO_OPTION,
];

// Format check shared with the per-dealer Preferences form (isValidFacts). A rule type
// with no pattern there (BUYER, LANE) is not format-checked rather than always failing.
export const factsFormatError = (facts, ruleType) => {
  if (isEmpty(facts) || !FACT_PATTERN_RULE_TYPES.includes(ruleType)) return null;
  if (isValidFacts(facts, ruleType)) return null;
  if (PreferencePatterns.RULE_TYPE_FACTS_NUMBER.includes(ruleType)) return PreferencePatterns.MSG_FACT_NUMBER;
  if (PreferencePatterns.RULE_TYPE_FACTS_YES_NO_OPTION.includes(ruleType)) {
    return PreferencePatterns.MSG_FACT_YES_NO_OPTION;
  }
  return PreferencePatterns.MSG_FACT_CHARACTER;
};

export const RULE_TYPE_REQUIRED = 'Select a rule type.';
export const OPERATOR_REQUIRED = 'Select an operator.';
export const FALLBACK_REQUIRED = 'Select what happens when this rule set does not match.';
export const RULE_SET_EMPTY = 'Add at least one rule, or remove this rule set.';
export const NO_RULE_SETS = 'Add at least one rule set before saving.';
export const RULES_INVALID = 'Some rules are incomplete or invalid — fix the highlighted fields before saving.';

// What APMS would reject (no operator / no fallback: 400) or silently never match (no
// facts: the block is always false and the master stops applying). Keyed by uid so the
// offending field can be pointed at, not just "something is wrong".
export const validateRules = (ruleSetList, ruleList) => {
  const ruleSets = {};
  const rules = {};

  ruleSetList.forEach(ruleSet => {
    const errors = {};
    if (isEmpty(ruleSet.defaultPreferenceRuleType)) errors.defaultPreferenceRuleType = FALLBACK_REQUIRED;
    if (isEmpty(ruleSet.items)) errors.items = RULE_SET_EMPTY;
    if (!isEmpty(errors)) ruleSets[ruleSet.uid] = errors;

    ruleSet.items
      .map(uid => ruleList.find(rule => rule.uid === uid))
      .filter(Boolean)
      .forEach(rule => {
        const ruleErrors = {};
        const facts = normalizeFactList(rule.facts);
        if (isEmpty(rule.ruleType)) ruleErrors.ruleType = RULE_TYPE_REQUIRED;
        if (isEmpty(rule.operatorType)) ruleErrors.operatorType = OPERATOR_REQUIRED;
        if (isEmpty(facts)) {
          ruleErrors.facts = PreferencePatterns.MSG_FACT_NULL;
        } else {
          const formatError = factsFormatError(facts, rule.ruleType);
          if (formatError) ruleErrors.facts = formatError;
        }
        if (!isEmpty(ruleErrors)) rules[rule.uid] = ruleErrors;
      });
  });

  return {
    ruleSets,
    rules,
    noRuleSets: isEmpty(ruleSetList),
    valid: isEmpty(ruleSetList) ? false : isEmpty(ruleSets) && isEmpty(rules),
  };
};

const PreferenceRulesEditor = ({ subscriptionId, preferenceType, onDirtyChange }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadedEmpty, setLoadedEmpty] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [ruleSetList, setRuleSetList] = useState([]);
  const [innerRuleSetList, setInnerRuleSetList] = useState([]);
  const [ruleList, setRuleList] = useState([]);
  // The text as typed, per rule uid, so "AAAI, " is not rewritten to "AAAI" mid-keystroke.
  // Rule state always holds the normalized list; the draft is dropped on blur so the field
  // then shows exactly what will be saved.
  const [factsDraft, setFactsDraft] = useState({});
  const [discover, setDiscover] = useState({ rules: [], operatorTags: [], defaultPreferencesRules: [] });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [dirty, setDirty] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!subscriptionId) return;
    setLoading(true);
    setLoadError(null);
    setLoadedEmpty(false);
    setSaveSuccess(false);
    setSaveError(null);
    setShowValidation(false);
    setFactsDraft({});
    setDirty(false);

    Promise.all([
      client.query({
        query: subscriptionPreferenceQuery,
        variables: { subscriptionId: Number(subscriptionId) },
        fetchPolicy: 'network-only',
      }),
      client.query({ query: preferencesDiscoverQuery, fetchPolicy: 'network-only' }),
    ]).then(([prefResult, discoverResult]) => {
      if (!mountedRef.current) return;
      const pref = prefResult.data && prefResult.data.subscriptionPreference;
      const loadedRuleSets = (pref && pref.ruleSetList) || [];
      setRuleSetList(loadedRuleSets);
      setInnerRuleSetList((pref && pref.innerRuleSetList) || []);
      setRuleList((pref && pref.ruleList) || []);
      // Every master is seeded with rules, and tappy answers an APMS failure with an
      // empty (or partial) preference rather than an error — so nothing at all coming
      // back is far more likely a failed read than a master with no rules.
      setLoadedEmpty(isEmpty(loadedRuleSets));
      setDiscover((discoverResult.data && discoverResult.data.preferencesDiscover) || {});
      setLoading(false);
    }).catch(() => {
      if (!mountedRef.current) return;
      setLoadError('Could not load the rules for this master.');
      setLoading(false);
    });
  }, [subscriptionId, reloadCount]);

  const markDirty = () => {
    setDirty(true);
    setSaveSuccess(false);
  };

  const rulesFor = ruleSet => ruleSet.items
    .map(uid => ruleList.find(rule => rule.uid === uid))
    .filter(Boolean);

  const innerSetsIn = ruleSet => ruleSet.items
    .filter(uid => innerRuleSetList.some(inner => inner.uid === uid));

  // Every rule and inner set under these items, however deep, so removing a rule set
  // does not leave its nested groups' rules behind in the payload.
  const descendantsOf = items => items.reduce((acc, uid) => {
    const inner = innerRuleSetList.find(ir => ir.uid === uid);
    if (!inner) return { ...acc, rules: [...acc.rules, uid] };
    const nested = descendantsOf(inner.items || []);
    return { rules: [...acc.rules, ...nested.rules], inner: [...acc.inner, uid, ...nested.inner] };
  }, { rules: [], inner: [] });

  const operatorOptionsFor = ruleType => {
    const found = (discover.operatorTags || []).find(tag => tag.ruleType === ruleType);
    return found ? found.value.map(val => ({ key: val, text: val, value: val })) : [];
  };

  const updateRule = (uid, changes) => {
    setRuleList(prev => prev.map(rule => (rule.uid === uid ? { ...rule, ...changes } : rule)));
    markDirty();
  };

  const onFactsChange = (uid, text) => {
    setFactsDraft(prev => ({ ...prev, [uid]: text }));
    updateRule(uid, { facts: normalizeFacts(text) });
  };

  const onFactsBlur = uid => {
    setFactsDraft(prev => omit(prev, uid));
  };

  const addRule = ruleSetUid => {
    const rule = EMPTY_RULE();
    setRuleList(prev => [...prev, rule]);
    setRuleSetList(prev => prev.map(rs => (
      rs.uid === ruleSetUid ? { ...rs, items: [...rs.items, rule.uid] } : rs
    )));
    markDirty();
  };

  const removeRule = (ruleSetUid, uid) => {
    setRuleList(prev => prev.filter(rule => rule.uid !== uid));
    setRuleSetList(prev => prev.map(rs => (
      rs.uid === ruleSetUid ? { ...rs, items: rs.items.filter(item => item !== uid) } : rs
    )));
    markDirty();
  };

  const addRuleSet = () => {
    setRuleSetList(prev => [...prev, { uid: nextUid('rs'), defaultPreferenceRuleType: '', items: [] }]);
    markDirty();
  };

  const removeRuleSet = ruleSetUid => {
    const target = ruleSetList.find(rs => rs.uid === ruleSetUid);
    if (target) {
      const { rules, inner } = descendantsOf(target.items);
      setRuleList(prev => prev.filter(rule => !rules.includes(rule.uid)));
      setInnerRuleSetList(prev => prev.filter(ir => !inner.includes(ir.uid)));
    }
    setRuleSetList(prev => prev.filter(rs => rs.uid !== ruleSetUid));
    markDirty();
  };

  const updateRuleSet = (uid, changes) => {
    setRuleSetList(prev => prev.map(rs => (rs.uid === uid ? { ...rs, ...changes } : rs)));
    markDirty();
  };

  const validation = validateRules(ruleSetList, ruleList);

  const onSave = () => {
    setSaveError(null);
    setSaveSuccess(false);
    if (!validation.valid) {
      setShowValidation(true);
      return;
    }
    setSaving(true);
    client.mutate({
      mutation: updatePreferencesMutation,
      variables: {
        preferenceInput: {
          preference: {
            id: String(subscriptionId),
            preferenceType,
            ruleSetList: JSON.stringify(
              ruleSetList.map(({ uid, defaultPreferenceRuleType, items }) => (
                { uid, defaultPreferenceRuleType, items }
              ))
            ),
            ruleList: JSON.stringify(
              ruleList.map(({
                uid, ruleType, facts, operatorType, include,
              }) => ({
                uid, ruleType, facts: normalizeFactList(facts), operatorType, include,
              }))
            ),
            innerRuleSetList: JSON.stringify(
              innerRuleSetList.map(({ uid, ruleType, items }) => ({ uid, ruleType, items }))
            ),
          },
        },
      },
    }).then(({ data }) => {
      if (!mountedRef.current) return;
      setSaving(false);
      const { success, errors } = data.updatePreferences;
      if (success) {
        setSaveSuccess(true);
        setShowValidation(false);
        setLoadedEmpty(false);
        setDirty(false);
      } else {
        setSaveError((errors && errors[0]) || 'Could not save the rules.');
      }
    }).catch(() => {
      if (!mountedRef.current) return;
      setSaving(false);
      setSaveError('Could not save the rules.');
    });
  };

  if (!subscriptionId) return null;

  const ruleTypeOptions = (discover.rules || []).map(r => ({ key: r.key, text: r.text, value: r.value }));
  const defaultRuleTypeOptions = (discover.defaultPreferencesRules || [])
    .map(r => ({ key: r.key, text: r.text, value: r.value }));

  return (
    <Segment data-testid='default-coverage-master-rules-editor'>
      <Header size='small'>
        Eligibility Rules
        <Header.Subheader>
          Where this master applies, as one or more fallback blocks evaluated top to bottom —
          the same mechanism and the same two blocks (EXCLUDE_RMS, then COVER_RMS) the
          DEDUCTIBLE pilot used for every dealer: EXCLUDE_RMS lists the channels and locations
          this master is limited to, and COVER_RMS re-opens coverage at specific locations
          even if the channel above would have excluded them. A block passes only when its
          fallback (EXCLUDE_RMS or COVER_RMS) passes AND every rule in it does, and the master
          applies if any block passes. A block with no rules is not a match-everything — APMS
          still evaluates its fallback — so every block here needs at least one complete rule.
        </Header.Subheader>
      </Header>

      {loading && <Loader active inline='centered' />}
      {loadError && <Message negative data-testid='default-coverage-master-rules-load-error'>{loadError}</Message>}

      {!loading && !loadError && loadedEmpty && (
        <Message warning icon data-testid='default-coverage-master-rules-empty-load'>
          <Icon name='warning sign' />
          <Message.Content>
            <Message.Header>No rules came back for this master</Message.Header>
            <p>
              Every master is created with rules, so this usually means they could not be read
              from APMS. Reload before editing — saving replaces the stored rules with exactly
              what is shown here.
            </p>
            <Button
              data-testid='default-coverage-master-rules-reload'
              size='small'
              basic
              onClick={() => setReloadCount(count => count + 1)}
            >
              <Icon name='refresh' /> Reload rules
            </Button>
          </Message.Content>
        </Message>
      )}

      {!loading && !loadError && (
        <Form>
          {ruleSetList.map(ruleSet => {
            const ruleSetErrors = (showValidation && validation.ruleSets[ruleSet.uid]) || {};
            const nestedCount = innerSetsIn(ruleSet).length;
            return (
              <Segment key={ruleSet.uid} secondary data-testid={`rule-set-${ruleSet.uid}`}>
                <Form.Group>
                  <Form.Field width={13} error={Boolean(ruleSetErrors.defaultPreferenceRuleType)}>
                    <label htmlFor={`default-rule-type-${ruleSet.uid}`}>When this doesn&apos;t match</label>
                    <Select
                      id={`default-rule-type-${ruleSet.uid}`}
                      data-testid={`rule-set-default-type-${ruleSet.uid}`}
                      placeholder='Select a fallback'
                      options={defaultRuleTypeOptions}
                      value={ruleSet.defaultPreferenceRuleType}
                      onChange={(e, { value }) => updateRuleSet(ruleSet.uid, { defaultPreferenceRuleType: value })}
                    />
                    {ruleSetErrors.defaultPreferenceRuleType && (
                      <div style={{ color: 'red' }} data-testid={`rule-set-default-type-error-${ruleSet.uid}`}>
                        {ruleSetErrors.defaultPreferenceRuleType}
                      </div>
                    )}
                  </Form.Field>
                  <Form.Field width={3} style={{ alignSelf: 'flex-end', textAlign: 'right' }}>
                    <Button
                      data-testid={`rule-set-remove-${ruleSet.uid}`}
                      size='small'
                      negative
                      basic
                      fluid
                      onClick={() => removeRuleSet(ruleSet.uid)}
                    >
                      <Icon name='trash' /> Remove rule set
                    </Button>
                  </Form.Field>
                </Form.Group>

                {nestedCount > 0 && (
                  <Message info size='small' data-testid={`rule-set-nested-note-${ruleSet.uid}`}>
                    This rule set also contains
                    {' '}
                    {nestedCount === 1 ? '1 nested AND/OR group' : `${nestedCount} nested AND/OR groups`}
                    . They are kept as they are when you save, but cannot be edited here.
                  </Message>
                )}

                {rulesFor(ruleSet).map(rule => {
                  const draft = factsDraft[rule.uid];
                  const shownFacts = draft !== undefined ? draft : (rule.facts || []).join(',');
                  const ruleErrors = (showValidation && validation.rules[rule.uid]) || {};
                  // A malformed fact is flagged as it is typed; a missing one only once
                  // Save is tried, so a freshly added rule does not open in red.
                  const factsError = ruleErrors.facts
                    || factsFormatError(normalizeFactList(rule.facts), rule.ruleType);
                  return (
                    <Segment key={rule.uid} tertiary data-testid={`rule-${rule.uid}`}>
                      <Form.Group>
                        <Form.Field width={5} error={Boolean(ruleErrors.ruleType)}>
                          <label htmlFor={`rule-type-${rule.uid}`}>Rule type</label>
                          <Select
                            id={`rule-type-${rule.uid}`}
                            data-testid={`rule-type-select-${rule.uid}`}
                            fluid
                            placeholder='Select a rule type'
                            options={ruleTypeOptions}
                            value={rule.ruleType}
                            onChange={(e, { value }) => updateRule(rule.uid, { ruleType: value, operatorType: '' })}
                          />
                          {ruleErrors.ruleType && (
                            <div style={{ color: 'red' }} data-testid={`rule-type-error-${rule.uid}`}>
                              {ruleErrors.ruleType}
                            </div>
                          )}
                        </Form.Field>
                        <Form.Field width={4} error={Boolean(ruleErrors.operatorType)}>
                          <label htmlFor={`rule-operator-${rule.uid}`}>Operator</label>
                          <Select
                            id={`rule-operator-${rule.uid}`}
                            data-testid={`rule-operator-select-${rule.uid}`}
                            fluid
                            placeholder='Select an operator'
                            options={operatorOptionsFor(rule.ruleType)}
                            value={rule.operatorType}
                            disabled={isEmpty(rule.ruleType)}
                            onChange={(e, { value }) => updateRule(rule.uid, { operatorType: value })}
                          />
                          {ruleErrors.operatorType && (
                            <div style={{ color: 'red' }} data-testid={`rule-operator-error-${rule.uid}`}>
                              {ruleErrors.operatorType}
                            </div>
                          )}
                        </Form.Field>
                        <Form.Field width={4}>
                          <label htmlFor={`rule-include-${rule.uid}`}>Include</label>
                          <Checkbox
                            id={`rule-include-${rule.uid}`}
                            data-testid={`rule-include-checkbox-${rule.uid}`}
                            toggle
                            checked={Boolean(rule.include)}
                            onChange={(e, { checked }) => updateRule(rule.uid, { include: checked })}
                          />
                        </Form.Field>
                        <Form.Field width={3} style={{ alignSelf: 'flex-end', textAlign: 'right' }}>
                          <Button
                            aria-label='Remove rule'
                            data-testid={`rule-remove-${rule.uid}`}
                            size='small'
                            icon
                            basic
                            onClick={() => removeRule(ruleSet.uid, rule.uid)}
                          >
                            <Icon name='close' /> Remove rule
                          </Button>
                        </Form.Field>
                      </Form.Group>
                      <Form.Field error={Boolean(factsError)}>
                        <label htmlFor={`rule-facts-${rule.uid}`}>Facts (comma-separated)</label>
                        <TextArea
                          id={`rule-facts-${rule.uid}`}
                          data-testid={`rule-facts-input-${rule.uid}`}
                          value={shownFacts}
                          rows={2}
                          style={{ width: '100%', fontFamily: 'monospace' }}
                          onChange={e => onFactsChange(rule.uid, e.target.value)}
                          onBlur={() => onFactsBlur(rule.uid)}
                        />
                        {factsError && (
                          <div style={{ color: 'red' }} data-testid={`rule-facts-error-${rule.uid}`}>
                            {factsError}
                          </div>
                        )}
                      </Form.Field>
                    </Segment>
                  );
                })}

                {ruleSetErrors.items && (
                  <div style={{ color: 'red' }} data-testid={`rule-set-empty-error-${ruleSet.uid}`}>
                    {ruleSetErrors.items}
                  </div>
                )}

                <Button
                  data-testid={`rule-add-${ruleSet.uid}`}
                  size='tiny'
                  basic
                  onClick={() => addRule(ruleSet.uid)}
                >
                  <Icon name='plus' /> Add rule
                </Button>
              </Segment>
            );
          })}

          <Button
            data-testid='default-coverage-master-rules-add-set'
            basic
            onClick={addRuleSet}
          >
            <Icon name='plus' /> Add rule set
          </Button>

          {showValidation && !validation.valid && (
            <Message negative data-testid='default-coverage-master-rules-invalid'>
              {validation.noRuleSets ? NO_RULE_SETS : RULES_INVALID}
            </Message>
          )}
          {saveError && (
            <Message negative data-testid='default-coverage-master-rules-save-error'>{saveError}</Message>
          )}
          {saveSuccess && (
            <Message positive data-testid='default-coverage-master-rules-save-success'>Rules saved</Message>
          )}

          <div style={{ marginTop: '1em' }}>
            <Button
              data-testid='default-coverage-master-rules-save'
              color='blue'
              loading={saving}
              disabled={saving}
              onClick={onSave}
            >
              <Icon name='save' /> Save Rules
            </Button>
          </div>
        </Form>
      )}
    </Segment>
  );
};

PreferenceRulesEditor.propTypes = {
  subscriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  preferenceType: PropTypes.string,
  // Told whenever the on-screen rules start or stop differing from what was last
  // loaded/saved, so the modal around this editor can stop a close from dropping them.
  onDirtyChange: PropTypes.func,
};

PreferenceRulesEditor.defaultProps = {
  subscriptionId: null,
  preferenceType: null,
  onDirtyChange: null,
};

export default PreferenceRulesEditor;
